import React, { useState, useEffect, useContext, useRef } from "react";
import { markerTypes } from "../../map/MapWithMarker";
import TouristicMarkerForm from "./TouristicMarkerForm";
import InformationMarkerForm from "./InformationMarkerForm";
import html2canvas from "html2canvas";
import UserContext from "../../../../src/UserContext";
import {
  DEFAULT_ICON_INDEX_ARRAY,
  DISTANCE_TO_TRIGGER_AUDIO,
  ICON_INDEX_DELETED,
  ICON_INDEX_PREFIX,
  URL_AUDIO,
  URL_VIDEO,
} from "../../map/GPX/Resources";
import { useTranslation } from "react-i18next";
import DialogBox from "../../util/DialogBox";
import LoadingBox, { closeLoadingBox } from "../../util/LoadingBox";
import { normalizeFileName, normalizeTitleSpaces } from "../../util/Util";
import { roadmakerApi } from "../../../api/roadmakerApi";
import {
  deletePoiMediaForMarker,
  discardPoiMediaUploads,
  queuePoiMediaUpload,
  republishMarkerMedia,
  stripServerMediaFromLegacyFields,
} from "../../../api/poiMediaUploads";
import {
  buildInitialImageIds,
  isChecksumAmongActiveImages,
  isImageChecksum,
  isReusableMainImageIds,
  mediaName,
  normalizeMediaName,
  orderImageIdsForReopen,
  orderMediaByMainImage,
  selectMainImage,
} from "../../../domain/mainImage";
import StyledText from "../../util/StyledText";

const MarkerEditionForm = (props) => {
  const {
    marker,
    selectedMarkerId,
    markers,
    setEditing,
    projectDatas,
    setEditedSubType,
    setMarkers,
    handleInputFocus,
    handleInputBlur,
    showConfigPOI,
    showRessourcePOI,
    showQuizPOI,
  } = props;
  const { t } = useTranslation();
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const MAX_ICON_SIZE = 5 * 1024 * 1024;
  const { iconIndexArray, setIconIndexArray } = useContext(UserContext);
  // Constat 2 (2e revue du 01/10) : aligné sur le helper isActiveMedia
  // (F1) — un média sans status est ACTIF, un média "deleting" est exclu.
  // L'ancien filtre media.url excluant seulement les médias SANS url
  // initialisait le formulaire avec une image en cours de suppression.
  const mediaForType = (mediaType) =>
    (marker.media || []).filter(
      (media) =>
        media.mediaType === mediaType && media.url && media.status !== "deleting"
    );
  const legacyList = (value) =>
    Array.isArray(value) ? value.filter(Boolean) : [];
  // Constat f19 (revue finale du 01/10) : la dérivation du nom d'affichage
  // d'un média (originalName, sinon fileName) vit dans le domaine — le
  // formulaire n'en garde plus de copie privée.
  const initialMediaValues = (legacyValues, mediaType) => [
    ...legacyList(legacyValues),
    ...mediaForType(mediaType).map((media) => media.url),
  ];
  const initialMediaNames = (legacyNames, mediaType) => [
    ...legacyList(legacyNames),
    ...mediaForType(mediaType).map(mediaName),
  ];
  const serverIcon = mediaForType("icon")[0];
  const initialImage = initialMediaValues(marker.image, "image");
  const initialVideo = initialMediaValues(marker.video, "video");
  const initialAudio = initialMediaValues(marker.audio, "audio");
  const initialImageName = initialMediaNames(marker.imageName, "image");
  const initialVideoName = initialMediaNames(marker.videoName, "video");
  const initialAudioName = initialMediaNames(marker.audioName, "audio");

  const [markerTitle, setMarkerTitle] = useState(
    marker.title ? marker.title : ""
  );
  const [markerSubType, setMarkerSubType] = useState("");
  const [markerDescription, setMarkerDescription] = useState(
    marker.description ? marker.description : ""
  );
  const [markerAudio, setMarkerAudio] = useState(initialAudio);
  const [markerVideo, setMarkerVideo] = useState(initialVideo);
  const [markerImage, setMarkerImage] = useState(initialImage);
  const [markerIcon, setMarkerIcon] = useState(
    serverIcon?.url || marker.iconImage || ""
  );
  const [iconName, setIconName] = useState(
    marker.iconName ? marker.iconName : ""
  );
  const [iconNameWithoutBorder, setIconNameWithoutBorder] = useState(
    marker.iconNameWithoutBorder
      ? marker.iconNameWithoutBorder
      : serverIcon
      ? mediaName(serverIcon)
      : ""
  );
  const [markerImageName, setMarkerImageName] = useState(initialImageName);
  const [markerVideoName, setMarkerVideoName] = useState(initialVideoName);
  const [markerAudioName, setMarkerAudioName] = useState(initialAudioName);

  const [markerAccesValue, setMarkerAccesValue] = useState(
    marker.checkAcces ? marker.checkAcces : ""
  );
  const [showInputImage, setShowInputImage] = useState(false);
  const [showInputVideo, setShowInputVideo] = useState(false);
  const [showInputAudio, setShowInputAudio] = useState(false);
  const [showInputIcon, setShowInputIcon] = useState(false);
  const [markerToDownload, setMarkerToDownload] = useState(
    marker.markerToDownload ? marker.markerToDownload : ""
  );
  const imageArray = initialImageName;
  const videoArray = initialVideoName;
  const audioArray = initialAudioName;
  const resources = [...imageArray, ...videoArray, ...audioArray];
  const [resourceArray, setResourceArray] = useState(
    marker.resourceArray ? marker.resourceArray : resources
  );
  const [mainResource, setMainResource] = useState(
    marker.mainResource || ""
  );
  // Lot « image principale » (2026-10-01, F2/F4/F6/F8) : une image
  // principale est TOUJOURS déterminée dès qu'il y a des images — choix
  // existant valide, sinon repli legacy mainResource (comparaison
  // tolérante), sinon première image ; "" quand il n'y en a aucune.
  // L'identité des images vit dans le tableau parallèle mainImageIds,
  // STRICTEMENT aligné sur markerImage/markerImageName (même longueur,
  // même index) : aucun recalcul par heuristique au rendu.
  const [mainImageIds, setMainImageIds] = useState(() => {
    // Constat 1 (2e revue du 01/10) : à la réouverture avant upload, le
    // tableau d'identités client (contentKeys des pièces en file) a été
    // persisté sous marker.mainImageIds (client-only, jamais envoyé — hors
    // whitelist POI_WRITE_FIELDS). Défaut élevé « réouverture » (revue
    // finale du 01/10), point 3 : il n'est réutilisé que s'il est ALIGNÉ
    // — même longueur que l'état initial ET, pour la partie serveur,
    // chaque id correspond au média serveur du même index après la
    // partie legacy (helper pur isReusableMainImageIds). Sinon (première
    // ouverture, état incohérent ou désordonné), reconstruction standard :
    // les pièces pending n'existent plus côté file après un flush, le
    // rebuild depuis le média trié reste correct.
    if (
      isReusableMainImageIds({
        mainImageIds: marker.mainImageIds,
        legacyNames: legacyList(marker.imageName),
        serverMedia: mediaForType("image"),
      })
    ) {
      return [...marker.mainImageIds];
    }
    return buildInitialImageIds({
      legacyNames: legacyList(marker.imageName),
      serverMedia: mediaForType("image"),
    });
  });
  const [mainImage, setMainImage] = useState(() =>
    selectMainImage({
      ids: mainImageIds,
      names: initialImageName,
      mainImage: marker.mainImage,
      mainResource: marker.mainResource,
    })
  );
  // Constat f4 (revue finale du 01/10) : l'état triggerType vit dans
  // TouristicMarkerForm (près du select) ; ce miroir permet à la
  // sauvegarde d'écrire markerToUpdate.triggerType sans re-décorer la
  // chaîne de props. Initialisé depuis le marker, mis à jour par le
  // composant enfant à chaque changement.
  const triggerTypeRef = useRef(
    marker.triggerType ? marker.triggerType : ""
  );
  const [triggerDistance, setTriggerDistance] = useState(
    marker.triggerDistance ? marker.triggerDistance : null
  );

  const [url, setUrl] = useState(marker.url ? marker.url : "");
  const [markerOpenQuestion, setMarkerOpenQuestion] = useState(
    marker.openQuestionArray && marker.openQuestionArray.length > 0
      ? marker.openQuestionArray
      : []
  );
  const [markerQcmArray, setMarkerQcmArray] = useState(
    marker.qcmArray && marker.qcmArray.length > 0 ? marker.qcmArray : []
  );

  const [markerQcmImageArray, setMarkerQcmImageArray] = useState(
    marker.qcmImageArray && marker.qcmImageArray.length > 0
      ? marker.qcmImageArray
      : []
  );

  const normalizeWebUrl = (value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : "";

    if (!trimmedValue) {
      return "";
    }

    if (/^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmedValue)) {
      return `https://${trimmedValue}`;
    }

    return trimmedValue;
  };

  const isValidWebUrl = (value) => {
    if (!value) {
      return true;
    }

    try {
      const parsedUrl = new URL(value);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    } catch {
      return false;
    }
  };

  /**
   * Sauvegarde les informations du marker
   * @param {event} event - Validation du formulaire
   */

  const saveMarkerInfos = (event) => {
    console.log("jest: Appeller la fonction saveMarkerInfos");
    event.preventDefault();

    // Compte le nombre de quizs incomplets
    const nbIncomplets =
      markerQcmArray.filter(
        (qcm) => qcm.correctAnswers.length === 0 && !qcm.noCorrectAnswer
      ).length +
      markerQcmImageArray.filter(
        (qcm) => qcm.correctAnswers.length === 0 && !qcm.noCorrectAnswer
      ).length +
      markerOpenQuestion.filter(
        (qcm) =>
          (!qcm.answer || qcm.answer.trim() === "") && !qcm.noCorrectAnswer
      ).length;

    if (nbIncomplets > 0) {
      DialogBox({
        icon: "warning",
        text: t(nbIncomplets === 1 ? "noAnswerQuiz" : "noAnswerQuizzes"),
        confirmButtonText: "OK",
      });
      return;
    }

    const normalizedUrl = normalizeWebUrl(url);

    if (!isValidWebUrl(normalizedUrl)) {
      DialogBox({
        icon: "warning",
        text: t("invalidWebUrl"),
        confirmButtonText: "OK",
      });
      return;
    }

    const markerToUpdate = markers.find(
      (marker) => marker.id === selectedMarkerId
    );
    if (markerToUpdate) {
      if (markerTitle) {
        markerToUpdate.title = normalizeTitleSpaces(markerTitle);
        setMarkerTitle("");
      }

      if (markerSubType) {
        markerToUpdate.subType = markerSubType;
        setMarkerSubType("");
      }

      if (markerDescription) {
        markerToUpdate.description = markerDescription;
        setMarkerDescription("");
      }

      // Resserrement du 01/10 : le bloc `if (mainResource)` ci-dessous a été
      // SUPPRIMÉ — l'écriture image-only qui suit est la SEULE source de
      // mainResource (nom de l'image principale, ou "" sans image ; un
      // audio/vidéo n'est plus jamais désigné principal).
      // Lot « image principale » (2026-10-01, F7) : la fonction est désormais
      // IMAGE SEULE. À chaque sauvegarde : avec image(s), mainResource = nom
      // de l'image principale et mainImage = son identité stable (checksum ;
      // un identifiant legacy ou une clé opaque n'est JAMAIS envoyé : le
      // backend n'accepte qu'un checksum SHA-256 hex minuscule ou "").
      // SANS image : mainResource="" ET mainImage="" — un audio/vidéo
      // historique n'est plus jamais désigné principal.
      if (markerImageName.length > 0) {
        const mainImageIndex = mainImageIds.indexOf(mainImage);
        const mainName =
          mainImageIndex >= 0 ? markerImageName[mainImageIndex] : markerImageName[0];
        markerToUpdate.mainResource = mainName || "";
      } else {
        markerToUpdate.mainResource = "";
        setMainResource("");
      }
      markerToUpdate.mainImage = isImageChecksum(mainImage) ? mainImage : "";

      if (
        markerAudio.every((audio) => audio === "") ||
        markerAudio.length > 0
      ) {
        markerToUpdate.audio = markerAudio;
      }

      if (
        markerVideo.every((video) => video === "") ||
        markerVideo.length > 0
      ) {
        markerToUpdate.video = markerVideo;
      }

      // Constat f5 (revue finale du 01/10) : assignations INCONDITIONNELLES
      // — les états tableaux du formulaire sont TOUJOURS des tableaux ; le
      // delete-all les vide en [] et la sauvegarde doit PERSISTER ce vide.
      // Les anciennes gardes (length > 0 / includes("")) laissaient les
      // anciennes valeurs sur markerToUpdate : l'image supprimée revenait
      // en fantôme après rechargement.
      markerToUpdate.image = markerImage;
      markerToUpdate.imageName = markerImageName;
      markerToUpdate.videoName = markerVideoName;
      markerToUpdate.audioName = markerAudioName;
      // Ne persister que les noms encore portés par un média du formulaire
      // (image, vidéo ou audio) — un nom supprimé ne doit pas rester fantôme.
      // Comparaison TOLÉRANTE : resourceArray porte des noms NORMALISÉS par
      // normalizeFileName (ex. "photo_1.png", ajoutés par les changements de
      // fichier) tandis que la réouverture reconstruit les noms du formulaire
      // depuis l'originalName BRUT du média serveur (ex. "Photo 1.png"). Un
      // filtre exact supprimait à tort le nom normalisé. "" n'entre jamais
      // dans le Set : un nom vide ne doit pas être conservé par erreur.
      const keptNormalized = new Set(
        [
          ...(Array.isArray(markerImageName) ? markerImageName : []),
          ...(Array.isArray(markerVideoName) ? markerVideoName : []),
          ...(Array.isArray(markerAudioName) ? markerAudioName : []),
        ]
          .map(normalizeMediaName)
          .filter((normalized) => normalized !== "")
      );
      markerToUpdate.resourceArray = (Array.isArray(resourceArray)
        ? resourceArray
        : []
      ).filter((item) => keptNormalized.has(normalizeMediaName(item)));
      // F7 : PLUS de repli mainResource = resourceArray[0] — la fonction
      // principale est image uniquement, un audio/vidéo en tête de
      // resourceArray ne doit jamais redevenir principale.

      if (markerIcon.includes("") || markerIcon.length > 0) {
        markerToUpdate.iconImage = markerIcon;
        markerToUpdate.iconName = iconName;
      }

      if (markerToDownload) {
        markerToUpdate.markerToDownload = markerToDownload;
      }

      if (
        iconNameWithoutBorder &&
        (iconNameWithoutBorder.includes("") || iconNameWithoutBorder.length > 0)
      ) {
        markerToUpdate.iconNameWithoutBorder = iconNameWithoutBorder;
        setIconNameWithoutBorder("");
      }

      if (markerAccesValue) {
        markerToUpdate.checkAcces = markerAccesValue;
        setMarkerAccesValue("");
      }
      // Constat f4 (revue finale du 01/10) : la valeur du sélecteur vit
      // dans TouristicMarkerForm — la sauvegarde lit le miroir ref.
      if (triggerTypeRef.current) {
        markerToUpdate.triggerType = triggerTypeRef.current;
      }
      if (triggerDistance) {
        markerToUpdate.triggerDistance = triggerDistance;
        setTriggerDistance(null);
      }
      markerToUpdate.url = normalizedUrl;

      setUrl(""); // Ne réinitialise l'URL que si elle a bien été mise à jour

      markerToUpdate.openQuestionArray = [...markerOpenQuestion];
      markerToUpdate.qcmArray = [...markerQcmArray];
      markerToUpdate.qcmImageArray = [...markerQcmImageArray];

      // F4d (retest 3 du 2026-09-04) : les états locaux d'affichage
      // contiennent les URLs des médias serveur ; les recopier tels quels
      // dans les champs legacy faisait afficher chaque média deux fois en
      // consultation (champ legacy + marker.media) jusqu'au rechargement.
      // Les médias normalisés vivent uniquement dans marker.media.
      const purged = stripServerMediaFromLegacyFields(markerToUpdate);
      if (purged !== markerToUpdate) {
        Object.assign(markerToUpdate, purged);
      }
      // Republier pour que la consultation reflète la sauvegarde sans
      // rechargement du parcours.
      // Constat 6 (2e revue du 01/10) : le tri miroir du DTO backend se
      // fait ICI à l'enregistrement (et non plus à la volée au choix) —
      // annuler ne mute rien, enregistrer met la consultation à jour.
      // Défaut élevé « réouverture » (revue finale du 01/10), point 1 :
      // le tri se fait AVANT le calcul de mainImageIds — le helper reçoit
      // les images serveur actives du markerToUpdate.media DÉJÀ trié (et
      // affichables selon le même contrat que le formulaire : url
      // présente, status ≠ deleting), PAS le marker (prop) périmé.
      markerToUpdate.media = orderMediaByMainImage(
        markerToUpdate.media,
        markerToUpdate.mainImage
      );
      const reopenedIds = orderImageIdsForReopen({
        formNames: markerImageName,
        formIds: mainImageIds,
        keptNames: markerToUpdate.imageName,
        serverMedia: markerToUpdate.media,
        mainImage: markerToUpdate.mainImage,
      });
      if (reopenedIds) {
        markerToUpdate.mainImageIds = reopenedIds;
      } else {
        delete markerToUpdate.mainImageIds;
      }
      republishMarkerMedia(markerToUpdate, setMarkers);
    }
    setEditing(false);
  };

  /**
   * Génère l'icône du marker
   */

  const handleDownloadIcon = () => {
    const element = document.getElementById("marker-icon");
    html2canvas(element)
      .then((canvas) => {
        const link = document.createElement("a");
        link.download = "markericon.png";
        link.href = canvas
          .toDataURL("image/png")
          .replace(/^data:image\/[^;]/, "data:application/octet-stream");
        document.body.appendChild(link);
        setMarkerToDownload(link.href);
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error(
          "Une erreur s'est produite lors de la génération de l'icône :",
          error
        );
      });
  };

  // Resserrement du 01/10 : l'ancien useEffect qui synchronisait
  // mainResource avec resourceArray (audio/vidéo) a été SUPPRIMÉ —
  // mainResource est IMAGE SEULE (F7) et vit avec le tableau parallèle
  // mainImageIds/mainImage ci-dessous, sans autre source d'écriture.

  // Lot « image principale » (2026-10-01, F2/F4/F6/F8) : mainImageIds est
  // le tableau parallèle STRICTEMENT aligné sur markerImageName — il n'est
  // JAMAIS recalculé au rendu, seulement muté aux mêmes indices que les
  // tableaux d'aperçus/noms. La réévaluation du choix survit aux
  // suppressions : la suppression de la principale sélectionne la première
  // image restante, la suppression de toutes remet "".
  useEffect(() => {
    setMainImage((previous) =>
      selectMainImage({
        ids: mainImageIds,
        names: markerImageName,
        mainImage: previous,
        mainResource,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainImageIds, markerImageName, mainResource]);

  // F2/F4/F6 : suppression d'une image du formulaire — retirer le MÊME
  // index des trois tableaux alignés (aperçus, noms, identités) et
  // abandonner la pièce en file par contentKey exact (jamais par nom).
  // Resserrement du 01/10 : setters FONCTIONNELS — chaque appel part de
  // l'état précédent (résiste aux clics rapprochés / stale closures).
  // Le retrier/republication serveur est géré par MarkerImage.
  const onRemoveImage = (index) => {
    const identity = mainImageIds[index];
    if (typeof identity === "string") {
      const discarded = discardPoiMediaUploads(
        marker.id,
        (item) => item.mediaType === "image" && item.contentKey === identity
      );
      // Défaut A : identité de repli (legacy-/server-) → rien retiré par
      // contentKey ; retirer AU PLUS UNE pièce image orpheline homonyme
      // de l'entrée (hors identités connues du formulaire).
      const name = markerImageName[index];
      if (discarded === 0 && typeof name === "string") {
        const knownKeys = new Set(mainImageIds);
        let fallbackTaken = false;
        discardPoiMediaUploads(marker.id, (item) => {
          if (
            fallbackTaken ||
            !item ||
            item.mediaType !== "image" ||
            knownKeys.has(item.contentKey)
          ) {
            return false;
          }
          const rawName = item.file?.name;
          const matches =
            rawName === name ||
            (typeof rawName === "string" &&
              normalizeFileName(rawName) === name);
          if (matches) fallbackTaken = true;
          return matches;
        });
      }
    }
    setMarkerImage((previous) => previous.filter((_, i) => i !== index));
    setMarkerImageName((previous) => previous.filter((_, i) => i !== index));
    setMainImageIds((previous) => previous.filter((_, i) => i !== index));
  };

  // Resserrement du 01/10 (constat 4) + constat f5 (revue finale) : la
  // suppression TOTALE des images réussie côté serveur vide les TROIS
  // tableaux alignés et les deux champs principaux. resourceArray est
  // filtré par la SAUVEGARDE contre les noms restants (union image/vidéo/
  // audio) : filtrer ici par markerImageName retirait aussi un audio ou
  // une vidéo homonyme de l'image supprimée.
  const onRemoveAllImages = () => {
    setMarkerImage([]);
    setMarkerImageName([]);
    setMainImageIds([]);
    setMainResource("");
    setMainImage("");
  };

  // Choix explicite (menu ou étoile) : enregistre l'identité ET le nom
  // lisible — mainResource reste le NOM de l'image choisie pour la
  // compatibilité legacy/export, jamais audio/vidéo.
  // Constat 6 (2e revue du 01/10) : PLUS de mutation de marker.media à la
  // volée — un choix suivi d'une ANNULATION ne doit rien laisser. La
  // consultation n'est mise à jour qu'à l'ENREGISTREMENT (tri + republish
  // dans saveMarkerInfos).
  const onChooseMainImage = (identity, name) => {
    setMainImage(identity);
    if (name) {
      setMainResource(name);
    }
  };

  // à améliorer (essayer promise au lieu du timeout)
  useEffect(() => {
    setTimeout(() => {
      handleDownloadIcon();
    }, 100);
  }, [markerIcon, markerAccesValue]);

  /**
   * Met à jour la distance du marker
   * @param {event} event - Modification de la distance
   */

  /**
   * Met à jour le nom de l'image
   * @param {event} event - Modification du nom de l'image
   */
  const updateImageName = (event) => {
    setMarkerImageName(event.target.value);
    setResourceArray(event.target.value);
  };

  /**
   * Met à jour le nom de la vidéo
   * @param {event} event - Modification du nom de la vidéo
   */
  const updateVideoName = (event) => {
    setMarkerVideoName(event.target.value);
    setResourceArray(event.target.value);
  };

  /**
   *  Met à jour le nom de l'audio
   * @param {event} event - Modification du nom de l'audio
   */
  const updateAudioName = (event) => {
    setMarkerAudioName(event.target.value);
    setResourceArray(event.target.value);
  };

  const updateIconNameWithoutBorder = (event) => {
    setIconNameWithoutBorder(event.target.value);
  };

  const handleDeleteIcon = async (event) => {
    event.stopPropagation();
    const serverIcon = (marker.media || []).find(
      (media) => media.mediaType === "icon"
    );
    if (serverIcon) {
      if (!marker.serverId || !marker.Project_id) return;
      try {
        await roadmakerApi.poiMedia.remove(
          marker.Project_id,
          marker.serverId,
          serverIcon
        );
      } catch (error) {
        console.error("Échec de la suppression du média serveur :", error);
        return;
      }
      marker.media = (marker.media || []).filter(
        (media) => media.fileName !== serverIcon.fileName
      );
    }
    const discarded = discardPoiMediaUploads(
      marker.id,
      (item) => item.mediaType === "icon"
    );
    if (!serverIcon && marker.serverId && discarded === 0) return;
    setMarkerIcon(serverIcon ? marker.iconImage || "" : "");
    setIconNameWithoutBorder("");
  };

  const handleDeleteAllMedia = (mediaType) =>
    deletePoiMediaForMarker(marker, mediaType);

  /**
   * Permet de mettre à jour l'image
   * @param {event} event - Modification de l'image
   */
  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        // Afficher un message d'erreur si la taille du fichier dépasse 5 Mo
        DialogBox({
          text: t("fileSizeError"),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      LoadingBox({
        text: t("loadingAdd"),
        icon: "info",
      });

      try {
        // Constat 4 (2e revue du 01/10) : le nom est capturé AVANT tout
        // await — un second changement de l'input pendant l'upload
        // écraserait event.target.value et le premier ajout prendrait le
        // nom du second.
        const filePath = event.target.value;
        const fileName = filePath.split("\\").pop().split("/").pop();
        const name = normalizeFileName(fileName);
        // Mise en file locale : l'envoi réel attend la sauvegarde du POI
        // (le backend exige un POI existant, ADR 0005). La valeur de retour
        // est le contentKey (SHA-256 dans le navigateur) : identité stable
        // de l'image, déjà disponible comme future mainImage.
        // F2 : null = contenu DÉJÀ en file (doublon) → rien à ajouter.
        // F10 : contenu déjà publié côté serveur → pas de second aperçu.
        const contentKey = await queuePoiMediaUpload(marker.id, "image", file);
        if (
          contentKey &&
          !isChecksumAmongActiveImages(marker.media, contentKey)
        ) {
          // Le fichier a été téléchargé avec succès
          const reader = new FileReader();
          reader.onloadend = () => {
            const newImage = reader.result;
            // Constat 4 (2e revue du 01/10) : setter FONCTIONNEL — deux
            // ajouts rapprochés partent du même rendu ; une closure sur
            // markerImage écraserait le premier aperçu avec le second.
            setMarkerImage((previous) => [...previous, newImage]);
          };
          reader.readAsDataURL(file);

          // F2/F4 : append id/preview/name ENSEMBLE — le tableau parallèle
          // reste strictement aligné (même index pour les trois).
          // Resserrement du 01/10 : setters FONCTIONNELS — chaque append
          // part de l'état précédent, les trois restent alignés.
          setMainImageIds((previous) => [...previous, contentKey]);
          setMarkerImageName((previous) => [...previous, name]);
          setResourceArray((previous) => [...previous, name]);
          setShowInputImage(false);
        } else if (contentKey) {
          // F10 : contenu déjà publié côté serveur — pas de second aperçu
          // et la pièce est RETIRÉE de la file (le média existe déjà sur le
          // serveur, un nouvel envoi ne ferait que dupliquer l'upload).
          // Constat f8 (revue finale du 01/10) : plus de fermeture
          // silencieuse — l'utilisateur est informé du refus.
          discardPoiMediaUploads(
            marker.id,
            (item) =>
              item.mediaType === "image" && item.contentKey === contentKey
          );
          setShowInputImage(false);
          DialogBox({
            text: t("duplicateImagePublished"),
            icon: "info",
            confirmButtonText: "OK",
          });
        } else {
          // F2 : contenu DÉJÀ en file (doublon) — rien à ajouter.
          // Constat f8 : plus de fermeture silencieuse — information.
          setShowInputImage(false);
          DialogBox({
            text: t("duplicateImageQueued"),
            icon: "info",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        console.error(
          "Une erreur s'est produite lors de la mise à jour de l'image:",
          error
        );
      } finally {
        // Fermer le loading box
        closeLoadingBox();
      }
    }
  };

  /**
   * Permet de mettre à jour l'audio
   * @param {event} event - Modification de l'audio
   */
  const handleAudioChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        // Afficher un message d'erreur si la taille du fichier dépasse 5 Mo
        DialogBox({
          text: t("fileSizeError"),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // Afficher le loading box
      LoadingBox({
        text: t("loadingAdd"),
        icon: "info",
      });
      try {
        // Mise en file locale : envoi réel différé à la sauvegarde du POI.
        await queuePoiMediaUpload(marker.id, "audio", file);
        const filePath = event.target.value;
        const fileName = filePath.split("\\").pop().split("/").pop();
        // Le fichier audio a été téléchargé avec succès
        const newAudio = URL_AUDIO + marker.id + normalizeFileName(fileName);
        setMarkerAudio([...markerAudio, newAudio]);
        setMarkerAudioName([...markerAudioName, normalizeFileName(fileName)]);
        setResourceArray([...resourceArray, normalizeFileName(fileName)]);
        setShowInputAudio(false);
      } catch (error) {
        console.error(
          "Une erreur s'est produite lors de la mise à jour de l'audio:",
          error
        );
      } finally {
        // fermeture de loading box
        closeLoadingBox();
      }
    }
  };

  /**
   * Permet de mettre à jour la vidéo
   * @param {event} event - Modification de la vidéo
   */
  const handleVideoChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        // Afficher un message d'erreur si la taille du fichier dépasse 5 Mo
        DialogBox({
          text: t("fileSizeError"),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // Afficher le loading box
      LoadingBox({
        text: t("loadingAdd"),
        icon: "info",
      });

      try {
        // Mise en file locale : envoi réel différé à la sauvegarde du POI.
        await queuePoiMediaUpload(marker.id, "video", file);
        const filePath = event.target.value;
        const fileName = filePath.split("\\").pop().split("/").pop();
        // Le fichier vidéo a été téléchargé avec succès
        const newVideo = URL_VIDEO + marker.id + normalizeFileName(fileName);
        setMarkerVideo([...markerVideo, newVideo]);
        setMarkerVideoName([...markerVideoName, normalizeFileName(fileName)]);
        setResourceArray([...resourceArray, normalizeFileName(fileName)]);
        setShowInputVideo(false);
      } catch (error) {
        console.error(
          "Une erreur s'est produite lors de la mise à jour de la vidéo:",
          error
        );
      } finally {
        // Fermer le loading box
        closeLoadingBox();
      }
    }
  };

  /**
   * Création du nom de l'icône
   */
  const createIconName = () => {
    let lastName, index, newIndex, iconName;
    lastName = iconIndexArray[iconIndexArray.length - 1];
    if (lastName === "Libre") return DEFAULT_ICON_INDEX_ARRAY;
    if (lastName.includes(ICON_INDEX_DELETED))
      index = parseInt(
        lastName.replace(ICON_INDEX_DELETED + ICON_INDEX_PREFIX, "")
      );
    else index = parseInt(lastName.replace(ICON_INDEX_PREFIX, ""));
    newIndex = index + 1;
    iconName = ICON_INDEX_PREFIX + newIndex;
    return iconName;
  };

  /**
   * Permet de mettre à jour l'icône
   * @param {event} event - Modification de l'icône
   */

  const handleIconChange = async (event) => {
    const file = event.target.files[0];
    if (file && file.size > MAX_ICON_SIZE) {
      DialogBox({
        text: t("iconSizeError"),
        icon: "error",
        confirmButtonText: "OK",
      });
      event.target.value = "";
      return;
    }
    handleDownloadIcon();

    if (file) {
      const serverIcon = (marker.media || []).find(
        (media) => media.mediaType === "icon"
      );
      // Une seule icône peut être en attente. La précédente icône serveur
      // n'est supprimée qu'après le POST réussi de la nouvelle.
      discardPoiMediaUploads(
        marker.id,
        (item) => item.mediaType === "icon"
      );
      await queuePoiMediaUpload(marker.id, "icon", file, {
        replaces: serverIcon,
      });
      // Le fichier icône a été téléchargé avec succès
      const reader = new FileReader();
      reader.onloadend = () => {
        setMarkerIcon(reader.result);
        setIconNameWithoutBorder(normalizeFileName(file.name));
        if (!marker.iconName || marker.iconName.length === 0) {
          setIconName(createIconName());
          iconIndexArray.push(createIconName()); // plus rapide que le hook setIconIndexArray [...iconIndexArray,createIconName()];
        }
      };
      reader.readAsDataURL(file);
      setShowInputIcon(false);
    }
  };
  return (
    <form onSubmit={saveMarkerInfos} data-testid="marker-form">
      {marker.type != markerTypes.touristic && (
        <>
          <label className="mt-4">{t("title")}</label>
          <StyledText
            markerText={markerTitle}
            setMarkerText={setMarkerTitle}
            testId="title-input"
          />

          <label className="mt-4">{t("description")}</label>
          <StyledText
            markerText={markerDescription}
            setMarkerText={setMarkerDescription}
            testId="description-input"
          />
        </>
      )}
      {marker.type === markerTypes.touristic && (
        <TouristicMarkerForm
          marker={marker}
          setMarkers={setMarkers}
          handleImageChange={handleImageChange}
          handleVideoChange={handleVideoChange}
          handleAudioChange={handleAudioChange}
          handleIconChange={handleIconChange}
          handleDeleteIcon={handleDeleteIcon}
          handleDeleteAllMedia={handleDeleteAllMedia}
          updateImageName={updateImageName}
          updateVideoName={updateVideoName}
          updateAudioName={updateAudioName}
          updateIconNameWithoutBorder={updateIconNameWithoutBorder}
          markerImage={markerImage}
          markerAudio={markerAudio}
          markerVideo={markerVideo}
          markerIcon={markerIcon}
          projectDatas={projectDatas}
          setMarkerAudio={setMarkerAudio}
          setMarkerImage={setMarkerImage}
          setMarkerVideo={setMarkerVideo}
          setMarkerIcon={setMarkerIcon}
          markerAccesValue={markerAccesValue}
          setMarkerAccesValue={setMarkerAccesValue}
          markerImageName={markerImageName}
          markerVideoName={markerVideoName}
          markerAudioName={markerAudioName}
          iconNameWithoutBorder={iconNameWithoutBorder}
          setMarkerImageName={setMarkerImageName}
          setMarkerVideoName={setMarkerVideoName}
          setMarkerAudioName={setMarkerAudioName}
          showInputImage={showInputImage}
          setShowInputImage={setShowInputImage}
          showInputAudio={showInputAudio}
          setShowInputAudio={setShowInputAudio}
          showInputVideo={showInputVideo}
          setShowInputVideo={setShowInputVideo}
          showInputIcon={showInputIcon}
          setShowInputIcon={setShowInputIcon}
          mainImage={mainImage}
          imageIds={mainImageIds}
          onChooseMainImage={onChooseMainImage}
          onRemoveImage={onRemoveImage}
          onRemoveAllImages={onRemoveAllImages}
          triggerTypeRef={triggerTypeRef}
          triggerDistance={triggerDistance}
          setTriggerDistance={setTriggerDistance}
          url={url}
          setUrl={setUrl}
          showConfigPOI={showConfigPOI}
          showRessourcePOI={showRessourcePOI}
          showQuizPOI={showQuizPOI}
          handleInputFocus={handleInputFocus}
          handleInputBlur={handleInputBlur}
          markerTitle={markerTitle}
          setMarkerTitle={setMarkerTitle}
          markerDescription={markerDescription}
          setMarkerDescription={setMarkerDescription}
          markerOpenQuestion={markerOpenQuestion}
          setMarkerOpenQuestion={setMarkerOpenQuestion}
          markerQcmArray={markerQcmArray}
          setMarkerQcmArray={setMarkerQcmArray}
          markerQcmImageArray={markerQcmImageArray}
          setMarkerQcmImageArray={setMarkerQcmImageArray}
        />
      )}
      {marker.type === markerTypes.information && (
        <InformationMarkerForm
          marker={marker}
          projectDatas={projectDatas}
          setMarkerTitle={setMarkerTitle}
          markerSubType={markerSubType}
          setMarkerSubType={setMarkerSubType}
          setEditedSubType={setEditedSubType}
          setMarkerDescription={setMarkerDescription}
        />
      )}

      <button
        type="submit"
        className="buttonModifMarker mt-2 w-50 p-2 "
        onClick={() => setEditedSubType(false)}
        aria-label="btn-enregistrer"
      >
        {t("save")}
      </button>
    </form>
  );
};

export default MarkerEditionForm;
